import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type {
  TotalRevenueBreakdownReference,
  TotalRevenueBreakdownTrendItem,
} from 'lumen/models';
import type {
  Category,
  SubCategory,
} from 'lumen/utils/constants/transaction-mapping/constants';
import {
  CATEGORY_TRANSLATIONS,
  SUB_CATEGORY_TRANSLATIONS,
} from 'lumen/utils/constants/transaction-mapping/translation-constants';

export default class TotalRevenueBreakdownItem extends Model {
  [Type] = 'total-revenue/breakdown-item' as const;

  /**
   * Department of the breakdown item. Only present in the source breakdown items.
   */
  @belongsTo('total-revenue/breakdown-item', {
    async: false,
    inverse: 'sources',
  })
  declare department?: TotalRevenueBreakdownItem;

  @hasMany('total-revenue/breakdown-reference', {
    async: false,
    inverse: 'totalRevenueItem',
  })
  declare referenceValues: HasMany<TotalRevenueBreakdownReference>;

  /**
   * Sources of the breakdown item. Only present in the department breakdown items.
   */
  @hasMany('total-revenue/breakdown-item', {
    async: false,
    inverse: 'department',
  })
  declare sources?: HasMany<TotalRevenueBreakdownItem>;

  /**
   * Daily trend items for the breakdown item. Only present in the department breakdown items.
   */
  @hasMany('total-revenue/breakdown/trend-item', {
    async: false,
    inverse: null,
  })
  declare trendDays: HasMany<TotalRevenueBreakdownTrendItem>;

  /**
   * Monthly trend items for the breakdown item. Only present in the department breakdown items.
   */
  @hasMany('total-revenue/breakdown/trend-item', {
    async: false,
    inverse: null,
  })
  declare trendMonths: HasMany<TotalRevenueBreakdownTrendItem>;

  /**
   * Weekly trend items for the breakdown item. Only present in the department breakdown items.
   */
  @hasMany('total-revenue/breakdown/trend-item', {
    async: false,
    inverse: null,
  })
  declare trendWeeks: HasMany<TotalRevenueBreakdownTrendItem>;

  @attr declare name: string;
  @attr('number') declare value: number;

  @service declare intl: Services['intl'];
  @service declare totalRevenue: Services['total-revenue'];

  @tracked capacity?: number;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return this.totalRevenue.formatValue(this.valueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedTrendItems() {
    return [...this.trendItems].sort(
      (a, b) => a.date.valueOf() - b.date.valueOf(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedCategory() {
    if (CATEGORY_TRANSLATIONS[this.name as Category]) {
      return this.intl.t(CATEGORY_TRANSLATIONS[this.name as Category]);
    }

    return this.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedSubCategory() {
    if (SUB_CATEGORY_TRANSLATIONS[this.name as SubCategory]) {
      return this.intl.t(SUB_CATEGORY_TRANSLATIONS[this.name as SubCategory]);
    }

    return this.name;
  }

  /**
   * Returns the trend items for the breakdown item based on the selected time range.
   * < 45 days => day aggregation
   * 45 - 90 days => week aggregation
   * > 90 days => month aggregation
   * Decided in https://otainsight.atlassian.net/browse/TOR-112
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get trendItems() {
    const trendDays = [...this.trendDays];

    if (trendDays.length < 45) {
      return trendDays;
    }

    if (trendDays.length < 90) {
      return [...this.trendWeeks];
    }

    return [...this.trendMonths];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueOrPAR() {
    return this.totalRevenue.isPerAvailableRoom
      ? this.totalRevenue.calculatePAR(this.value, this.capacity)
      : this.value;
  }
}
